import * as z from "zod";
import type { ApiContext } from "../context";
import {
  AccountNotFound,
  AdminRemovalRefused,
  type ApiNotAuthorized,
  ErrorCode,
  LiveRefused,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { AdminClosedStream, AdminLiveStream, AdminQueuedBox, LiveCancelledBreak } from "../model/live";
import { ShopImportPreview, ShopImportResult } from "../model/shop-import";
import { PlatformAdmin, UserProfile } from "../model/user";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

const UserEnvelope = z.object({ user: UserProfile });
const AdminLiveStreamsEnvelope = z.object({ streams: z.array(AdminLiveStream) });
const AdminQueuedBoxesEnvelope = z.object({ boxes: z.array(AdminQueuedBox) });
const ActionResponse = z.object({ ok: z.boolean() });
const PlatformAdminsEnvelope = z.object({ admins: z.array(PlatformAdmin) });

/** Platform staff: `/admin/*`. Every route but `bootstrap` answers 401 to a non-admin. */
export class AdminApi {
  constructor(private readonly t: Transport) {}

  /** The caller's profile, if they are ADMIN; ApiNotAuthorized otherwise. */
  async me(ctx: ApiContext): Promise<Result<UserProfile, ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/me");
    return forward(
      r,
      (j) => parse(UserEnvelope, j).user,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /**
   * Trades the one-shot bootstrap token for ADMIN on the signed-in caller.
   * Available only while the environment has no admin; every failure (unarmed,
   * expired, wrong, already spent) is the same ApiNotAuthorized.
   */
  async bootstrap(ctx: ApiContext, token: string): Promise<Result<UserProfile, ApiNotAuthorized>> {
    const r = await this.t.post(ctx, "/admin/bootstrap", { body: { token } });
    return forward(
      r,
      (j) => parse(UserEnvelope, j).user,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Every platform admin, the caller marked `isSelf`. */
  async admins(ctx: ApiContext): Promise<Result<PlatformAdmin[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/admins");
    return forward(
      r,
      (j) => parse(PlatformAdminsEnvelope, j).admins,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /**
   * Makes an existing account a platform admin. The account must have signed in
   * once; adding an admin twice changes nothing. Returns the new list.
   */
  async addAdmin(ctx: ApiContext, email: string): Promise<Result<PlatformAdmin[], ApiNotAuthorized | AccountNotFound>> {
    const r = await this.t.post(ctx, "/admin/admins", { body: { email } });
    return forward(
      r,
      (j) => parse(PlatformAdminsEnvelope, j).admins,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "email" && e.code === ErrorCode.DoesntExist) return new AccountNotFound(email, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Takes platform admin away. Never your own. Returns the new list. */
  async removeAdmin(
    ctx: ApiContext,
    userId: string,
  ): Promise<Result<PlatformAdmin[], ApiNotAuthorized | AdminRemovalRefused>> {
    const r = await this.t.delete(ctx, `/admin/admins/${userId}`);
    return forward(
      r,
      (j) => parse(PlatformAdminsEnvelope, j).admins,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "userId") return new AdminRemovalRefused(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Resolves a scraped shop directory against the database and reports what
   * would happen. Writes nothing, and runs the same normalizer as `importShops`,
   * so the preview is the plan.
   */
  async previewShopImport(ctx: ApiContext, csv: string): Promise<Result<ShopImportPreview, ApiNotAuthorized>> {
    const r = await this.t.post(ctx, "/admin/shop-import/preview", { body: { csv } });
    return forward(
      r,
      (j) => parse(ShopImportPreview, j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Applies the plan: one unclaimed store per row, idempotent on the source's own id. */
  async importShops(ctx: ApiContext, csv: string): Promise<Result<ShopImportResult, ApiNotAuthorized>> {
    const r = await this.t.post(ctx, "/admin/shop-import/commit", { body: { csv } });
    return forward(
      r,
      (j) => parse(ShopImportResult, j),
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /**
   * Every live stream across every store, newest first, capped at 200.
   *
   * There is no merchant counterpart to it: running a break is store work
   * behind an ownership check, and staff have no business drawing someone
   * else's teams. They can only shut a stream down or call its boxes off.
   *
   * `admin.test.ts` sells a spot through the real Stripe path and asserts the
   * row's gross and platform fee against what that order actually took, rather
   * than against a seeded number.
   */
  async liveStreams(ctx: ApiContext): Promise<Result<AdminLiveStream[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/live/streams");
    return forward(
      r,
      (j) => parse(AdminLiveStreamsEnvelope, j).streams,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Unfinished boxes selling from a shop's queue, across every store. */
  async liveQueuedBoxes(ctx: ApiContext): Promise<Result<AdminQueuedBox[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/live/boxes");
    return forward(
      r,
      (j) => parse(AdminQueuedBoxesEnvelope, j).boxes,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Broken and cancelled boxes across every store, in a stream or not. */
  async liveFinishedBoxes(ctx: ApiContext): Promise<Result<AdminQueuedBox[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/live/boxes/finished");
    return forward(
      r,
      (j) => parse(AdminQueuedBoxesEnvelope, j).boxes,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /**
   * Ends a stream on its host's behalf. With `refundBoxes` every unfinished box
   * is cancelled first and its paid buyers refunded; otherwise a rip is
   * finished and waiting boxes go back to the shop's queue.
   */
  async closeLiveStream(
    ctx: ApiContext,
    streamId: string,
    o: { refundBoxes: boolean },
  ): Promise<Result<AdminClosedStream, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/admin/live/streams/${streamId}/close`, { body: o });
    return forward(
      r,
      (j) => parse(AdminClosedStream, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Cancels one box and refunds its paid buyers. Again retries refunds Stripe refused. */
  async cancelLiveBox(
    ctx: ApiContext,
    breakId: string,
  ): Promise<Result<LiveCancelledBreak, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.delete(ctx, `/admin/live/breaks/${breakId}`);
    return forward(
      r,
      (j) => parse(LiveCancelledBreak, j),
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }

  /** Deletes a finished box for good. Production refuses one with a paid order. */
  async removeLiveBox(ctx: ApiContext, breakId: string): Promise<Result<void, ApiNotAuthorized | LiveRefused>> {
    const r = await this.t.post(ctx, `/admin/live/breaks/${breakId}/remove`);
    return forward(
      r,
      (j) => {
        parse(ActionResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) return new LiveRefused(e.code, e.detailedDesc, e.details, e);
        return e;
      },
    );
  }
}
